import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import CountdownSection from './CountdownSection.tsx';
import RSVPSection from './RSVPSection.tsx';
import WishesSection from './WishesSection.tsx';
import { 
  EVENT_DATE,
  EVENT_TIME,
  VENUE_NAME,
  VENUE_ADDRESS,
  EVENT_HASHTAG,
  GREETING,
  INVITATION_TEXT,
  GROOM_NAME,
  GROOM_FULL_NAME,
  BRIDE_NAME,
  BRIDE_FULL_NAME,
  GROOM_FATHER_NAME,
  GROOM_MOTHER_NAME,
  BRIDE_FATHER_NAME,
  BRIDE_MOTHER_NAME,
} from '../constants/event.ts';
import { COLORS } from '../constants/colors.ts';
import { fadeIn, slideUp, staggerChildren } from '../constants/animations.ts';

interface MainContentProps {
  rsvpSectionRef: React.RefObject<HTMLElement | null>;
}

const EVENT_MONTH = EVENT_DATE.toLocaleString('en-US', { month: 'long' });
const EVENT_DAY_NUM = EVENT_DATE.getDate();
const EVENT_YEAR = EVENT_DATE.getFullYear();

const MainContent: React.FC<MainContentProps> = ({ rsvpSectionRef }) => {
  const [wishRefreshKey, setWishRefreshKey] = useState(0);
  const handleWishSubmitted = useCallback(() => setWishRefreshKey(k => k + 1), []);

  return (
    <>
      {/* Invitation Card Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerChildren}
        className="relative"
        style={{ backgroundColor: COLORS.primary }}
      >
        {/* Card */}
        <div className="mx-5 my-8 relative" style={{ backgroundColor: COLORS.secondary }}>
          {/* Gold border */}
          <div className="absolute inset-0 pointer-events-none z-10 border-[12px]" style={{ borderColor: COLORS.accent }} />

          <motion.div variants={slideUp} className="relative z-20 px-10 py-12 text-center">
            {/* Bismillah */}
            <p className="font-alex-brush text-3xl font-semibold leading-tight mb-12" style={{ color: COLORS.accent }}>
              {GREETING}
            </p>

            {/* Parents */}
            <div className="space-y-3 mb-6">
              <div className="space-y-1">
                <p className="font-optima text-xs tracking-[0.12em] uppercase font-semibold leading-relaxed" style={{ color: COLORS.accent }}>
                  {GROOM_FATHER_NAME}
                </p>
                <p className="font-optima text-xs font-semibold leading-relaxed" style={{ color: COLORS.accent }}>
                  &amp;
                </p>
                <p className="font-optima text-xs tracking-[0.12em] uppercase font-semibold leading-relaxed" style={{ color: COLORS.accent }}>
                  {GROOM_MOTHER_NAME}
                </p>
              </div>
              <p className="font-optima text-sm italic" style={{ color: `${COLORS.accent}99` }}>
                together with
              </p>
              <div className="space-y-1">
                <p className="font-optima text-xs tracking-[0.12em] uppercase font-semibold leading-relaxed" style={{ color: COLORS.accent }}>
                  {BRIDE_FATHER_NAME}
                </p>
                <p className="font-optima text-xs font-semibold leading-relaxed" style={{ color: COLORS.accent }}>
                  &amp;
                </p>
                <p className="font-optima text-xs tracking-[0.12em] uppercase font-semibold leading-relaxed" style={{ color: COLORS.accent }}>
                  {BRIDE_MOTHER_NAME}
                </p>
              </div>
            </div>

            {/* Invitation text */}
            <p className="font-optima text-sm italic leading-relaxed mb-10" style={{ color: `${COLORS.accent}99` }}>
              {INVITATION_TEXT}
            </p>

            {/* Divider */}
            <div className="my-8 flex items-center justify-center gap-3">
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
            </div>

            {/* Groom */}
            <div className="space-y-6 mb-6">
              <h2 className="font-alex-brush text-2xl font-semibold leading-snug" style={{ color: COLORS.accent }}>
                {GROOM_NAME}
              </h2>
              <p className="font-optima text-xs tracking-[0.2em] uppercase" style={{ color: COLORS.accent }}>
                {GROOM_FULL_NAME.replace(`${GROOM_NAME} `, '').toUpperCase()}
              </p>
            </div>

            <p className="font-optima text-xl my-3" style={{ color: COLORS.accent }}>&</p>

            {/* Bride */}
            <div className="space-y-6 mb-12">
              <h2 className="font-alex-brush text-2xl font-semibold leading-snug" style={{ color: COLORS.accent }}>
                {BRIDE_NAME}
              </h2>
              <p className="font-optima text-xs tracking-[0.2em] uppercase" style={{ color: COLORS.accent }}>
                {BRIDE_FULL_NAME.replace(`${BRIDE_NAME} `, '').toUpperCase()}
              </p>
            </div>

            {/* Divider */}
            <div className="my-8 flex items-center justify-center gap-3">
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
            </div>

            {/* Venue */}
            <div className="mb-8">
              <p className="font-optima text-sm tracking-[0.15em] uppercase font-bold mb-3" style={{ color: COLORS.accent }}>
                {VENUE_NAME}
              </p>
              <p
                className="font-optima text-sm leading-relaxed"
                style={{ color: `${COLORS.accent}99` }}
                dangerouslySetInnerHTML={{ __html: VENUE_ADDRESS.replace(/\n/g, '<br />') }}
              />
            </div>

            {/* Divider */}
            <div className="my-8 flex items-center justify-center gap-3">
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
            </div>

            {/* Date & Time */}
            <div className="mb-8 space-y-2">
              <p className="font-optima text-sm" style={{ color: COLORS.accent }}>
                Saturday, {EVENT_DAY_NUM} {EVENT_MONTH} {EVENT_YEAR}
              </p>
              <p className="font-optima text-lg font-semibold" style={{ color: COLORS.accent }}>
                {EVENT_TIME}
              </p>
            </div>

            {/* Divider */}
            <div className="my-8 flex items-center justify-center gap-3">
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: `${COLORS.accent}55` }} />
              <div className="h-px w-16" style={{ backgroundColor: `${COLORS.accent}55` }} />
            </div>

            {/* Dress Code */}
            <div className="space-y-2">
              <p className="font-optima text-xs tracking-[0.25em] uppercase font-bold" style={{ color: COLORS.accent }}>
                Dress Code
              </p>
              <p className="font-optima text-sm" style={{ color: COLORS.accent }}>
                Tuxedo &amp; Dress or Traditional Attire
              </p>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Countdown Section */}
      <CountdownSection weddingDate={EVENT_DATE} />

      {/* Hashtag Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-16 text-center"
        style={{ backgroundColor: COLORS.primary }}
      >
        <motion.div variants={slideUp} className="space-y-4 px-6">
          <h3 className="font-optima text-xs tracking-[0.25em] uppercase font-bold" style={{ color: COLORS.accent }}>Share Your Moments</h3>
          <p className="font-optima text-3xl font-semibold" style={{ color: COLORS.secondary }}>
            {EVENT_HASHTAG}
          </p>
        </motion.div>
      </motion.section>

      {/* RSVP Section */}
      <RSVPSection rsvpSectionRef={rsvpSectionRef} onWishSubmitted={handleWishSubmitted} />

      {/* Wishes Display Section */}
      <WishesSection refreshKey={wishRefreshKey} />

      {/* Footer */}
      <motion.footer 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-6 mb-12 space-y-2"
        style={{ backgroundColor: COLORS.primary, color: COLORS.secondary }}
      >
        <p className="text-center text-sm opacity-75 pb-5">© 2026 Strada. All rights reserved.</p>
      </motion.footer>
    </>
  );
};

export default MainContent;
